import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../../contexts/UserContext';
import { signInWithEmail, resendVerificationForUser } from './auth';
import { auth } from '../../services/firebase';
import './Auth.css';

const Login = () => {
  const navigate = useNavigate();
  const { updateUser } = useContext(UserContext);
  
  // State
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [unverifiedUser, setUnverifiedUser] = useState(null);
  
  // Login
  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    
    try {
      const result = await signInWithEmail(email, password);
      
      if (result.success) {
        // Successful login with verified email
        const { user, userData } = result;
        
        // Update user in context
        updateUser({
          uid: user.uid,
          email: user.email,
          emailVerified: user.emailVerified,
          ...userData
        });
        
        // Check if user has completed Digital Card setup
        if (!userData.curationSignature || userData.curationSignature.signalWeights === undefined) {
          // Redirect to Digital Card setup if not completed
          navigate('/card-generator');
        } else {
          // Redirect to discover feed if setup is complete
          navigate('/discover');
        }
      } else if (result.needsVerification) {
        // Email needs verification
        setUnverifiedUser(result.user);
        setNeedsVerification(true);
      } else {
        // Login failed
        setError(result.error);
      }
    } catch (error) {
      console.error('Unexpected error during login:', error);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Resend verification email
  const handleResendVerification = async () => {
    setLoading(true);
    setError('');
    
    try {
      let result;
      
      if (unverifiedUser) {
        // Use the unverified user from login attempt
        result = await resendVerificationForUser(unverifiedUser);
      } else {
        // Fallback: try to get current user
        const user = auth.currentUser;
        if (user) {
          result = await resendVerificationForUser(user);
        } else {
          setError('Unable to resend verification email. Please try logging in again.');
          setLoading(false);
          return;
        }
      }
      
      if (result.success) {
        setVerificationSent(true);
      } else {
        setError(result.error || 'Failed to send verification email. Please try again.');
      }
    } catch (error) {
      console.error('Error sending verification email:', error);
      setError('Failed to send verification email. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // If needs email verification
  if (needsVerification) {
    return (
      <div className="auth-container centered">
        <div className="auth-form-container">
          <h2>Email Verification Required</h2>
          
          {!verificationSent ? (
            <>
              <p>
                Your email address hasn't been verified yet. Please check your inbox for a verification email, 
                or click below to resend the verification email.
              </p>
              
              <button 
                className="auth-button"
                onClick={handleResendVerification}
                disabled={loading}
              >
                {loading ? 'Sending...' : 'Resend Verification Email'}
              </button>
            </>
          ) : (
            <>
              <div className="auth-message">
                Verification email sent! Please check your inbox and follow the instructions to verify your email.
              </div>
              <p>
                After verifying your email, you can <button onClick={() => window.location.reload()} className="text-button">refresh this page</button> to log in.
              </p>
            </>
          )}
          
          <div className="auth-footer">
            <button 
              className="text-button"
              onClick={() => {
                auth.signOut();
                setNeedsVerification(false);
                setUnverifiedUser(null);
                setVerificationSent(false);
              }}
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  // Normal login form
  return (
    <div className="auth-container centered">
      <div className="auth-form-container">
        <h2>Welcome Back to Artifex</h2>
        <p className="auth-description">
          Log in to continue your curated design discovery experience.
        </p>
        
        {error && <div className="auth-error">{error}</div>}
        
        <form onSubmit={handleLogin} className="auth-form">
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
            />
          </div>
          
          <button 
            type="submit" 
            className="auth-button"
            disabled={loading}
          >
            {loading ? 'Logging In...' : 'Log In'}
          </button>
        </form>
        
        <div className="auth-links">
          <p>
            <button 
              type="button"
              onClick={(e) => {
                e.preventDefault();
                navigate('/forgot-password');
              }} 
              className="text-button"
            >
              Forgot Password?
            </button>
          </p>
          <p>
            Don't have an account? <button 
              type="button"
              onClick={(e) => {
                e.preventDefault();
                navigate('/signup');
              }} 
              className="text-button"
            >Sign up</button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;